/* Multi-template architecture for Lumetier tenant public sites.

   Each template is a complete component bundle: Layout + 5 pages
   (Home, Portfolio, Services, Booking, Contact). Tenants pick a
   template_id in Site Settings → Templates and the entire public site
   re-renders in that template's structure (kept settings, kept theme).

   Adding a new template = drop a new folder in /app/frontend/src/templates/
   and register it here. The contract: every template MUST export
   Layout + Home + Portfolio + Services + Booking + Contact + BookingConfirmation
   (any subset can re-export the editorial one as a fallback during dev). */

import EditorialLayout from "@/templates/editorial/Layout";
import EditorialHome from "@/templates/editorial/Home";
import EditorialPortfolio from "@/templates/editorial/Portfolio";
import EditorialServices from "@/templates/editorial/Services";
import EditorialBooking from "@/pages/Booking";
import EditorialBookingConfirmation from "@/pages/BookingConfirmation";
import EditorialContact from "@/templates/editorial/Contact";

import BoldLayout from "@/templates/bold/Layout";
import BoldHome from "@/templates/bold/Home";
import BoldPortfolio from "@/templates/bold/Portfolio";
import BoldServices from "@/templates/bold/Services";
import BoldBooking from "@/templates/bold/Booking";
import BoldContact from "@/templates/bold/Contact";

import MinimalLayout from "@/templates/minimal/Layout";
import MinimalHome from "@/templates/minimal/Home";
import MinimalPortfolio from "@/templates/minimal/Portfolio";
import MinimalServices from "@/templates/minimal/Services";
import MinimalBooking from "@/templates/minimal/Booking";
import MinimalContact from "@/templates/minimal/Contact";

import JournalLayout from "@/templates/journal/Layout";
import JournalHome from "@/templates/journal/Home";
import JournalPortfolio from "@/templates/journal/Portfolio";
import JournalServices from "@/templates/journal/Services";
import JournalBooking from "@/templates/journal/Booking";
import JournalContact from "@/templates/journal/Contact";

// v2 unique Layouts + per-template Home / Portfolio / Services / Contact
// bundles. Each of the six v2 templates now ships a fully bespoke set of
// inner pages so their structural identity carries through every surface
// a visitor sees, not just the home hero. The Booking flow continues to
// share the editorial component (it's a pure data form, not a branded
// canvas) so we keep one tested checkout path across the whole product.
import BrutalistLayout from "@/templates/brutalist/Layout";
import BrutalistHome from "@/templates/brutalist/Home";
import BrutalistPortfolio from "@/templates/brutalist/Portfolio";
import BrutalistServices from "@/templates/brutalist/Services";
import BrutalistContact from "@/templates/brutalist/Contact";

import SpotlightLayout from "@/templates/spotlight/Layout";
import SpotlightHome from "@/templates/spotlight/Home";
import SpotlightPortfolio from "@/templates/spotlight/Portfolio";
import SpotlightServices from "@/templates/spotlight/Services";
import SpotlightContact from "@/templates/spotlight/Contact";

import AtelierLayout from "@/templates/atelier/Layout";
import AtelierHome from "@/templates/atelier/Home";
import AtelierPortfolio from "@/templates/atelier/Portfolio";
import AtelierServices from "@/templates/atelier/Services";
import AtelierContact from "@/templates/atelier/Contact";

import HeirloomLayout from "@/templates/heirloom/Layout";
import HeirloomHome from "@/templates/heirloom/Home";
import HeirloomPortfolio from "@/templates/heirloom/Portfolio";
import HeirloomServices from "@/templates/heirloom/Services";
import HeirloomContact from "@/templates/heirloom/Contact";

import VistaLayout from "@/templates/vista/Layout";
import VistaHome from "@/templates/vista/Home";
import VistaPortfolio from "@/templates/vista/Portfolio";
import VistaServices from "@/templates/vista/Services";
import VistaContact from "@/templates/vista/Contact";

import BotanicalLayout from "@/templates/botanical/Layout";
import BotanicalHome from "@/templates/botanical/Home";
import BotanicalPortfolio from "@/templates/botanical/Portfolio";
import BotanicalServices from "@/templates/botanical/Services";
import BotanicalContact from "@/templates/botanical/Contact";

// Template definitions — id, label, description, status, component bundle.
// Status="ready" templates show as picker cards. Status="coming" templates
// show in the picker but are disabled with a "Coming soon" tag.
export const TEMPLATES = {
  editorial: {
    id: "editorial",
    label: "Editorial",
    tagline: "Cinematic. Massive serifs. Italic gold accents.",
    description: "Full-bleed cinematic hero, asymmetric portfolio mosaic, generous whitespace. Best for weddings, portraits, and brand work.",
    status: "ready",
    previewColor: "#C9A961",
    components: {
      Layout: EditorialLayout,
      Home: EditorialHome,
      Portfolio: EditorialPortfolio,
      Services: EditorialServices,
      Booking: EditorialBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: EditorialContact,
    },
  },
  minimal: {
    id: "minimal",
    label: "Minimal",
    tagline: "Whitespace-heavy. Sans-serif. Quiet confidence.",
    description: "Centered single hero image, strict 3-column portfolio grid, small everything. Best for newborn, family, and fine art.",
    status: "ready",
    previewColor: "#A8A29E",
    components: {
      Layout: MinimalLayout,
      Home: MinimalHome,
      Portfolio: MinimalPortfolio,
      Services: MinimalServices,
      Booking: MinimalBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: MinimalContact,
    },
  },
  bold: {
    id: "bold",
    label: "Bold",
    tagline: "Dark canvas. Type-led hero. Magazine energy.",
    description: "No photo in the hero — just oversized type. Photo reveal on scroll. Pinterest-style masonry portfolio. Best for fashion, creative directors.",
    status: "ready",
    previewColor: "#0A0908",
    components: {
      Layout: BoldLayout,
      Home: BoldHome,
      Portfolio: BoldPortfolio,
      Services: BoldServices,
      Booking: BoldBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: BoldContact,
    },
  },
  journal: {
    id: "journal",
    label: "Journal",
    tagline: "Magazine layout. Mixed serif + sans. Caption-driven.",
    description: "Photo-and-text spreads like a print magazine. Story-driven portfolio with captions on every shot. Best for documentary and travel.",
    status: "ready",
    previewColor: "#7A6F5A",
    components: {
      Layout: JournalLayout,
      Home: JournalHome,
      Portfolio: JournalPortfolio,
      Services: JournalServices,
      Booking: JournalBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: JournalContact,
    },
  },

  // ── v2 templates ─────────────────────────────────────────────────────────
  // Six additional templates that ship as styled variants of the Editorial
  // structural skeleton — they reuse Editorial's component bundle and get
  // their distinctive feel from the companion theme palette + typeface pair
  // applied automatically by the admin TemplateSection on selection
  // (see SUGGESTED_THEMES in /pages/admin/site/_shared.jsx). The structural
  // skeleton stays consistent (proven layout, no surprises) while each
  // variant feels meaningfully different in mood and niche fit. Any one of
  // these can be deepened to a unique Layout later once studios actually
  // pick it. ────────────────────────────────────────────────────────────
  atelier: {
    id: "atelier",
    label: "Atelier",
    tagline: "Intimate. Romantic. Liquid rose gold.",
    description: "High-contrast Playfair serif on warm blush canvas. Built for boudoir, intimate portraits, and bridal — the rooms where photographs feel like secrets.",
    status: "ready",
    previewColor: "#A18066",
    niche: "Boudoir · Intimate · Bridal",
    components: {
      Layout: AtelierLayout,
      Home: AtelierHome,
      Portfolio: AtelierPortfolio,
      Services: AtelierServices,
      Booking: EditorialBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: AtelierContact,
    },
  },
  heirloom: {
    id: "heirloom",
    label: "Heirloom",
    tagline: "Soft, paper-warm, hand-made.",
    description: "Caramel + cream + classic Cormorant — the album you'd actually print. For newborns, families, and milestones that mean something a decade from now.",
    status: "ready",
    previewColor: "#B8967D",
    niche: "Newborn · Family · Milestones",
    components: {
      Layout: HeirloomLayout,
      Home: HeirloomHome,
      Portfolio: HeirloomPortfolio,
      Services: HeirloomServices,
      Booking: EditorialBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: HeirloomContact,
    },
  },
  vista: {
    id: "vista",
    label: "Vista",
    tagline: "Wide horizons. Cool air. Magazine-cover energy.",
    description: "Coastal blue and seafoam with airy Playfair display. Built for landscape, travel, and adventure photographers whose work is best seen large.",
    status: "ready",
    previewColor: "#5EAAA8",
    niche: "Landscape · Travel · Adventure",
    components: {
      Layout: VistaLayout,
      Home: VistaHome,
      Portfolio: VistaPortfolio,
      Services: VistaServices,
      Booking: EditorialBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: VistaContact,
    },
  },
  brutalist: {
    id: "brutalist",
    label: "Brutalist",
    tagline: "Strict grid. Monospace. No decoration.",
    description: "Near-black canvas, signal amber accents, JetBrains Mono throughout. For the photographer who treats their site like a technical specification — fashion editors, art directors, and conceptual studios.",
    status: "ready",
    previewColor: "#F59E0B",
    niche: "Editorial · Conceptual · Art Direction",
    components: {
      Layout: BrutalistLayout,
      Home: BrutalistHome,
      Portfolio: BrutalistPortfolio,
      Services: BrutalistServices,
      Booking: EditorialBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: BrutalistContact,
    },
  },
  botanical: {
    id: "botanical",
    label: "Botanical",
    tagline: "Verdant. Garden-press. Hand-typeset.",
    description: "Forest sage + linen white with Fraunces' organic curves. For wedding florists' favorite photographers, outdoor portraitists, and any studio that smells like fresh-cut eucalyptus.",
    status: "ready",
    previewColor: "#7D9E68",
    niche: "Outdoor · Florals · Lifestyle",
    components: {
      Layout: BotanicalLayout,
      Home: BotanicalHome,
      Portfolio: BotanicalPortfolio,
      Services: BotanicalServices,
      Booking: EditorialBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: BotanicalContact,
    },
  },
  spotlight: {
    id: "spotlight",
    label: "Spotlight",
    tagline: "Cinematic dark. Theatrical. Poster-headline.",
    description: "Midnight canvas, DM Serif Display headlines hammered in champagne gold. For the studio whose portfolio belongs on a movie marquee — sports, performance, music, and high-drama editorial work.",
    status: "ready",
    previewColor: "#C9A961",
    niche: "Sports · Performance · Music",
    components: {
      Layout: SpotlightLayout,
      Home: SpotlightHome,
      Portfolio: SpotlightPortfolio,
      Services: SpotlightServices,
      Booking: EditorialBooking,
      BookingConfirmation: EditorialBookingConfirmation,
      Contact: SpotlightContact,
    },
  },
};

export const TEMPLATE_IDS = Object.keys(TEMPLATES);
export const DEFAULT_TEMPLATE_ID = "editorial";

/** Resolve a template id to its component bundle. Falls back to the
 *  editorial template if the requested id is unknown or hasn't shipped yet. */
export function resolveTemplate(templateId) {
  const t = TEMPLATES[templateId] || TEMPLATES[DEFAULT_TEMPLATE_ID];
  if (t.status !== "ready" || !t.components) {
    return TEMPLATES[DEFAULT_TEMPLATE_ID].components;
  }
  return t.components;
}
